import * as THREE from 'three';
import { getTableLayout, ballRadius } from '../sim/world.js';
import { railTopZ, railCenterX, railCenterY } from './table-rails.js';

/**
 * テーブル装飾マーク（自前・GPL 非依存）:
 *   - クロス上: ヘッドスポット / フットスポット / 中央スポットの小さな白丸
 *   - レール（木枠）上面: ダイヤモンドサイト（白い菱形〜小円）長辺6個・短辺3個ずつ
 *
 * ダイヤモンドサイトは標準的な台で「レール上面の中央線上」に等間隔で埋め込まれる。
 * 9ft プール台はテーブル比 2:1 なので、ダイヤ間隔の単位は長辺・短辺で共通（halfX/4 = halfY/2）。
 *   - 長辺: テーブル長を 8 等分した内側 7 点のうち中央（サイドポケット位置）を除く 6 点
 *   - 短辺: テーブル幅を 4 等分した内側 3 点（中央＋両側の 1/4 位置）
 */
export const addTableMarks = (scene: THREE.Scene): void => {
  const { halfX, halfY } = getTableLayout();

  // ---- クロス上のスポットシール ----
  const clothSpotMat = new THREE.MeshBasicMaterial({
    color: 0xeae6dc,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
  });
  const clothSpotGeo = new THREE.CircleGeometry(0.006, 20);
  const spotZ = -ballRadius + 0.0008; // クロス上面のわずかに上（z-fighting 回避）

  const addClothSpot = (x: number, y: number): void => {
    const mesh = new THREE.Mesh(clothSpotGeo, clothSpotMat);
    mesh.position.set(x, y, spotZ);
    scene.add(mesh);
  };
  addClothSpot(-halfX / 2, 0); // ヘッドスポット
  addClothSpot(halfX / 2, 0); // フットスポット
  addClothSpot(0, 0); // 中央スポット

  // ---- レール上面のダイヤモンドサイト ----
  // マザーオブパール風の白い小円。視認できるサイズに拡大。
  const sightMat = new THREE.MeshStandardMaterial({
    color: 0xf5f1e3,
    roughness: 0.28,
    metalness: 0.15,
    envMapIntensity: 0.9,
  });
  const sightGeo = new THREE.CircleGeometry(0.009, 20);
  const sightZ = railTopZ() + 0.001; // レール（木枠）上面のすぐ上

  const addSight = (x: number, y: number): void => {
    const m = new THREE.Mesh(sightGeo, sightMat);
    m.position.set(x, y, sightZ);
    // CircleGeometry はデフォルトで法線 +z（xy 平面） → 上面に向く
    scene.add(m);
  };

  // 長辺レール（y = ±railCenterY）に 6 個。x = -halfX..halfX を 8 等分した内側 7 点のうち
  // i=4（中央）はサイドポケット位置なので除外し、残り 6 点を配置。
  const ryNorth = railCenterY(halfY);
  const rySouth = -ryNorth;
  for (let i = 1; i <= 7; i++) {
    if (i === 4) continue;
    const x = -halfX + (2 * halfX) * (i / 8);
    addSight(x, ryNorth);
    addSight(x, rySouth);
  }
  // 短辺レール（x = ±railCenterX）に 3 個。y = -halfY..halfY を 4 等分した内側 3 点。
  // 9ft 台はテーブル比 2:1 なので、ここでの間隔（halfY/2）は長辺の間隔（halfX/4）と等しい。
  const rxEast = railCenterX(halfX);
  const rxWest = -rxEast;
  for (let j = 1; j <= 3; j++) {
    const y = -halfY + (2 * halfY) * (j / 4);
    addSight(rxEast, y);
    addSight(rxWest, y);
  }
};
